import { Component, ElementRef, Input, OnInit, Renderer2, ViewChild } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute } from '@angular/router';
import { Browser } from '@capacitor/browser';
import { Store } from '@ngxs/store';
import { ImageItem } from 'ng-gallery';
import { LoggerService } from 'src/app/ax-commons-logger/logger/logger.service';
import { AppState } from 'src/app/ngxs/app/app.state';
import { AxUiService } from 'src/app/services/ax-ui.service';
import { PhotoViewerService } from '../../../services/photo-viewer.service';
import { DocumentsService } from '../../../services/documents.service';
import { PrependServerToFileSrcPipe } from '../../../pipes/prepend-server-to-file-src.pipe';
import { WorkComment, WorkCommentsService, WorkCreateCommentDto, WorkDocument, WorkMyService, WorkNews, WorkNewsReadConfirmation, WorkNewsV2Service } from '../../../_generated/work-rest-api';

@Component({
  selector: 'app-news-detail',
  templateUrl: './news-detail.page.html',
  styleUrls: ['./news-detail.page.scss'],
})
export class NewsDetailPage implements OnInit {
  @Input() newsItem: WorkNews;
  @ViewChild('contentContainer', { static: false }) contentContainer: ElementRef;
  public newComment = '';
  public createNewCommentLabel = 'Neuen Kommentar schreiben';
  public recipientsString = '';
  public images = [];
  public attachments: WorkDocument[] = [];
  public isEmployee = false;
  /** Lesestand der angemeldeten Person zu dieser News (weich und ggf. hart). */
  public readConfirmation: WorkNewsReadConfirmation;
  public isConfirmingRead = false;
  private mutationObserver: MutationObserver;

  constructor(
    private photoViewerService: PhotoViewerService,
    private renderer: Renderer2,
    private axMyService: WorkMyService,
    private activatedRoute: ActivatedRoute,
    private title: Title,
    private axUiService: AxUiService,
    private store: Store,
    private axCommentsService: WorkCommentsService,
    private documentsService: DocumentsService,
    private prependServerToFileSrcPipe: PrependServerToFileSrcPipe,
    private axNewsV2Service: WorkNewsV2Service,
  ) {
  }

  /**
   * Sichtbar, solange die News eine aktive Lesebestaetigung verlangt und diese noch aussteht.
   *
   * Das blosse Oeffnen (weicher Lesestand) reicht dafuer bewusst nicht.
   */
  public get showConfirmationRequest(): boolean {
    return !!this.newsItem?.requestReadConfirmation && !this.readConfirmation?.confirmedAt;
  }

  ngAfterViewInit() {
    this.initializeMutationObserver();
    this.store.select(AppState.profile).subscribe(profile => this.isEmployee = profile.isUserEntity);
  }

  ngOnDestroy() {
    if (this.mutationObserver) {
      this.mutationObserver.disconnect();
    }
  }

  ngOnInit() {
    if (!this.newsItem) {
      this.activatedRoute.params.subscribe(data => {
        this.load(data.id);
      });
    }
  }

  public load(newsId: number) {
    this.axMyService.myNewsV1ControllerFindOneNews({ id: newsId })
      .subscribe(newsItem => {
        newsItem.text = this.removeTargetAttributes(newsItem.text);
        this.newsItem = newsItem;
        this.setCreateNewCommentsLabel();
        this.setRecipientsLabel();
        this.setImageGalleryAndAttachments();
        this.markAsRead(newsId);
      });
  }

  /**
   * Weicher Lesestand: die News wurde in der App geoeffnet.
   *
   * Der Endpoint ist idempotent. Ein Fehler wird nur geloggt — ein fehlgeschlagener Lesestand darf das Lesen
   * nicht stoeren.
   */
  private markAsRead(newsId: number) {
    this.axNewsV2Service.newsV2ControllerMarkAsRead({ id: newsId }).subscribe({
      next: readConfirmation => this.readConfirmation = readConfirmation,
      error: markAsReadException => LoggerService.LOG(this, 'markAsReadException', markAsReadException),
    });
  }

  /** Harte Lesebestaetigung: die Person bestaetigt aktiv, die News gelesen zu haben. */
  public confirmRead() {
    this.isConfirmingRead = true;
    this.axNewsV2Service.newsV2ControllerConfirmRead({ id: this.newsItem.id }).subscribe({
      next: readConfirmation => {
        this.readConfirmation = readConfirmation;
        this.isConfirmingRead = false;
        this.axUiService.showSuccess('Lesebestätigung erfasst.');
      },
      error: () => {
        this.isConfirmingRead = false;
        this.axUiService.showError('Lesebestätigung konnte nicht erfasst werden.');
      },
    });
  }

  public async openAttachment(document: WorkDocument) {
    if (document.name && document.name.toLowerCase().indexOf('.pdf') > -1) {
      await this.documentsService.openPdf(document);
    } else {
      await this.photoViewerService.openAxovaDocumentImage(document);
    }
  }

  private initializeMutationObserver() {
    this.mutationObserver = new MutationObserver(() => {
      this.bindClickEvents();
    });

    this.mutationObserver.observe(this.contentContainer.nativeElement, {
      childList: true,
      subtree: true,
    });
  }

  private bindClickEvents() {
    const figures = this.contentContainer.nativeElement.querySelectorAll('figure');
    figures.forEach((figure: HTMLElement) => {
      const img = figure.querySelector('img');
      const figcaption = figure.querySelector('figcaption');

      if (img) {
        const imageUrl = img.getAttribute('src');
        const captionText = figcaption ? figcaption.textContent : '';

        this.renderer.listen(figure, 'click', () => {
          console.log('click', imageUrl, captionText);
          this.photoViewerService.openImage(imageUrl, captionText).catch();
        });
      }
    });
  }

  public openLink(link: string): void {
    Browser.open({ url: link });
  }

  ionViewDidEnter() {
    this.title.setTitle('News Details');
  }

  public sendComment(answer?: Partial<WorkComment>) {
    console.log('answer', answer);
    const body: WorkCreateCommentDto = {
      comment: this.newComment,
      newsId: this.newsItem.id,
    };
    if (answer) {
      body.comment = answer.comment;
      body.parentId = answer.parentId;
    }
    this.axCommentsService.commentsV1ControllerCreate({
      body,
    }).subscribe({
      next: () => {
        this.axUiService.showSuccess('Kommentar wurde gespeichert!');
        this.load(this.newsItem.id);
        this.newComment = '';
      },
      error: error => {
        this.axUiService.showError('Kommentar konnte nicht gespeichert werden.');
      },
    });
  }

  private setCreateNewCommentsLabel() {
    try {
      if (this.store.selectSnapshot(AppState.profile).meta.title.id === 2) {
        this.createNewCommentLabel = 'Schreibe als Erste einen Kommentar';
      } else {
        this.createNewCommentLabel = 'Schreibe als Erster einen Kommentar';
      }
    } catch (noCommentsException) {
      LoggerService.LOG(this, 'noCommentsException', noCommentsException);
    }
  }

  private setRecipientsLabel() {
    try {
      this.recipientsString = this.newsItem?.audiences.map(audience => audience.name).join(', ');
    } catch (noAudiencesException) {
      LoggerService.LOG(this, 'noAudiencesException', noAudiencesException);
    }
  }

  private setImageGalleryAndAttachments() {
    const documents: WorkDocument[] = this.newsItem?.documents ?? [];
    const images = [];
    const attachments: WorkDocument[] = [];
    for (const document of documents) {
      if (document.mimetype && document.mimetype.startsWith('image/')) {
        images.push(
          new ImageItem({
            src: this.prependServerToFileSrcPipe.transform(document.fileSrcOptimized ?? document.fileSrc),
            thumb: this.prependServerToFileSrcPipe.transform(document.fileSrcThumbnail ?? document.fileSrc),
            alt: document.name ?? 'Bild wird geladen',
          }),
        );
      } else {
        attachments.push(document);
      }
    }
    this.images = images;
    this.attachments = attachments;
  }

  private removeTargetAttributes(html: string): string {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const links = doc.querySelectorAll('[target]');
    links.forEach(link => {
      const href = link.getAttribute('href');
      if (href.indexOf('http') === -1) {
        link.removeAttribute('target');
      }
    });
    return new XMLSerializer().serializeToString(doc);
  }
}
